import React, { useMemo, useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import classNames from 'classnames';
import { useTranslation } from 'next-i18next';
import { debounce, trim, sortedUniq } from 'lodash-es';

import { IDoc, ECmsId } from '../../../api/investorApi';
import NavAnchor, { AnchorProvider } from '../../../components/NavAnchor';
import Switch from '../../../components/Switch';
import Search from '../../../components/Search';
import ScrollPosition, { IScrollTopContext } from '../../../components/ScrollPosition';
import { useScreenSize, BreakPoint } from '../../../components/ScreenSize';

import InvestorBlock from '../InvestorBlock';
import ContentList from '../ContentList';
import { OverallPdfCard, CoveredPdfCard } from '../PdfCard';
import Banner from './Banner';

interface IProps {
  type: 'covered' | 'uncovered';
  title: string;
  cmsId?: ECmsId;
  years: string[];
  content: IDoc[];
}

type Direction = 'row' | 'col';

function DocList(props: IProps) {
  const { title, type, years, content } = props;
  const { screen } = useScreenSize();
  const router = useRouter();
  const { locale }: any = router;
  const [listType, setListType] = useState<Direction>('row');
  const [keyword, setKeyword] = useState('');
  const [yearList, setYearList] = useState(years);
  const [docs, setDocs] = useState(content);
  const [isStick, setIsStick] = useState(false);
  const [scrollTop, setScrollTop] = useState(0);
  const isMobile = screen === BreakPoint.md || screen === BreakPoint.sm;

  const { t } = useTranslation('investor');
  const titleIR = t('titleIR');
  const listTypeOptions = useMemo(() => {
    return [
      {
        text: t('tabCard'),
        value: 'row',
      },
      {
        text: t('tabList'),
        value: 'col',
      },
    ];
  }, [t]);

  const breadcrumb = useMemo(() => {
    if (locale === 'zh-CN') {
      return [{ text: titleIR, link: `/investor-relations` }, { text: title }, { text: title }];
    }
    return [{ text: titleIR, link: `/${locale}/investor-relations` }, { text: title }, { text: title }];
  }, [title, titleIR, locale]);

  const currDocs = useMemo(() => {
    const _searchKey = trim(keyword);
    if (!_searchKey) return docs;

    return docs.filter((doc) => {
      return `${doc.title}#${doc.subTitle}#${doc.date}`.indexOf(_searchKey) >= 0;
    });
  }, [docs, keyword]);

  const currYears = useMemo(() => {
    const _searchKey = trim(keyword);
    if (!_searchKey) return yearList;

    return sortedUniq(currDocs.map((doc) => doc.year));
  }, [yearList, keyword, currDocs]);

  const sectionList = useMemo(() => {
    return currYears.map((year) => ({
      title: year,
      anchor: year,
    }));
  }, [currYears]);

  useEffect(() => {
    setDocs(content);
    setYearList(years);
  }, [content, years]);

  const handleListTypeChange = (opt: { value: string }) => {
    setListType(opt.value as Direction);
  };

  const handleSearch = (kw: string) => {
    setKeyword(kw);
  };

  const handleStickScroll = (position: IScrollTopContext) => {
    const { scrollTop: top } = position;
    /**
     * [BreakPoint.lg, BreakPoint.xl] -> 262 - 60
     * [BreakPoint.xl, Infinity] -> 262 - 80
     * [0, BreakPoint.lg] -> 186
     */
    switch (screen) {
      case BreakPoint.lg:
        setIsStick(top >= 202);
        break;
      case BreakPoint.xl:
      case BreakPoint['2xl']:
        setIsStick(top >= 182);
        break;
      default:
        setIsStick(top >= 126);
    }
  };

  const handleNavTop = useMemo(
    () =>
      debounce((position: IScrollTopContext) => {
        const { scrollTop: top } = position;
        setScrollTop(top);
      }, 30),
    [],
  );

  const handleScroll = (position: IScrollTopContext) => {
    handleStickScroll(position);
    handleNavTop(position);
  };

  const wrapperCls = useMemo(() => {
    return classNames(
      {
        'overflow-x-hidden': isMobile, // 保证positon: sticky生效，只有移动端才添加hidden
      },
      `w-full bg-[#FAFBFB] px-0 pb-[60px] lg:pt-[50px] lg:pb-[100px]`,
    );
  }, [isMobile]);

  const containerCls = useMemo(() => {
    return `my-0 mx-auto w-full lg:px-5 xl:px-0 xl:max-w-[1280px] flex flex-col lg:flex-row`;
  }, []);

  const navWrapperCls = useMemo(() => {
    return classNames('w-full lg:w-[220px] relative', { 'h-[42px]': isStick && isMobile });
  }, [isStick, isMobile]);

  const navStickyWrapperCls = useMemo(() => {
    return classNames({
      'fixed sticky lg:top-[110px] xl:top-[130px]': isStick && !isMobile,
      'fixed top-[60px] bg-[#fff] w-full z-[10001] box-s': isStick && isMobile,
    });
  }, [isStick, isMobile]);

  const contentCls = useMemo(() => {
    return 'flex-1 px-5 lg:px-0 mt-[20px] lg:mt-0';
  }, []);

  return (
    <AnchorProvider defaultAnchor={currYears[0]} scrollTop={scrollTop}>
      <ScrollPosition onScroll={handleScroll}>
        {/** 容器尺寸 > xl ==> pt-20，否则pt-15 */}
        <Banner breadcrumb={breadcrumb} />

        <div className={wrapperCls}>
          <div className={containerCls}>
            <div className={navWrapperCls}>
              <div className={navStickyWrapperCls}>
                <NavAnchor content={sectionList} />
              </div>
            </div>

            <div className={contentCls}>
              <div className="mb-[40px] flex items-center justify-between">
                {type === 'uncovered' && (
                  <Switch value={listType} options={listTypeOptions} onChange={handleListTypeChange} />
                )}
                <Search onSearch={handleSearch} placeholder={t('placeholderSearchKeyword')} />
              </div>

              <div className="mt-[-20px] lg:mt-[-68px]">
                {(currYears || []).map((year: string) => {
                  return (
                    <InvestorBlock title={year} anchor={year} key={`${year}_${listType}_${keyword}`}>
                      <ContentList direction={listType} colNum={type === 'covered' ? 4 : 3}>
                        {(currDocs || [])
                          .filter((c) => c.year === year)
                          .map((c) => {
                            if (type === 'covered') {
                              return <CoveredPdfCard key={c.id} {...c} />;
                            }
                            return <OverallPdfCard key={c.id} {...c} expand={listType === 'col'} />;
                          })}
                      </ContentList>
                    </InvestorBlock>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </ScrollPosition>
    </AnchorProvider>
  );
}

export { DocList };
